import { Handle, Position } from '@xyflow/react';
import { memo } from 'react';
import { Zap, MessageSquare, Image, Video, FileText, MousePointer2, ExternalLink, Phone } from 'lucide-react';

const TriggerNode = ({ data }) => {
  const isTemplate = data.triggerType === 'template_response';
  const template = data.template;

  return (
    <div className="bg-white rounded-2xl shadow-xl border-2 border-primary-500 overflow-hidden min-w-[280px] transition-all hover:shadow-2xl">
      {/* Header Bar */}
      <div className="bg-primary-500 p-3 flex items-center gap-3">
        <div className="bg-white/20 p-1.5 rounded-lg">
          <Zap className="h-5 w-5 text-white fill-white" />
        </div>
        <div>
          <h3 className="text-white font-bold text-sm leading-tight">
            Trigger
          </h3>
          <p className="text-white/70 text-[10px] font-medium uppercase tracking-wider">
            {isTemplate ? 'Template Reply' : 'Keyword Match'}
          </p>
        </div>
        <div className="ml-auto">
          <div className="h-2 w-2 rounded-full bg-white animate-pulse" />
        </div>
      </div>

      <div className="p-4">
        {isTemplate ? (
          <div className="space-y-3">
            {/* Template Card Preview */}
            <div className="bg-gray-50 rounded-xl border border-gray-200">
              {/* Header Preview */}
              {template?.header && (
                <div className="bg-gray-100 p-3 border-b border-gray-200 flex items-center gap-2 text-gray-500">
                  {template.header.format === 'TEXT' ? (
                    <span className="text-[10px] font-bold uppercase truncate max-w-full">
                      {template.header.text}
                    </span>
                  ) : (
                    <>
                      {template.header.format === 'IMAGE' && <Image size={14} />}
                      {template.header.format === 'VIDEO' && <Video size={14} />}
                      {template.header.format === 'DOCUMENT' && <FileText size={14} />}
                      <span className="text-[10px] font-bold uppercase">
                        {template.header.format}
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Body Preview */}
              <div className="p-3">
                <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed line-clamp-4">
                  {template?.body?.text || 'No template body content'}
                </p>
                {template?.footer && (
                  <p className="text-[10px] text-gray-400 mt-2">
                    {template.footer.text}
                  </p>
                )}
              </div>

              {/* Buttons Preview & Handles */}
              {template?.buttons && template.buttons.length > 0 ? (
                <div className="border-t border-gray-200">
                  {template.buttons.map((btn, index) => (
                    <div
                      key={index}
                      className="relative flex items-center justify-center p-3 border-b last:border-b-0 border-gray-50 group hover:bg-white transition-colors"
                    >
                      <span className="text-xs font-bold text-primary-600 flex items-center gap-2">
                        {btn.type === 'quick_reply' && <MousePointer2 size={12} className="text-primary-400" />}
                        {btn.type === 'url' && <ExternalLink size={12} className="text-primary-400" />}
                        {btn.type === 'phone_number' && <Phone size={12} className="text-primary-400" />}
                        {btn.text}
                      </span>
                      
                      {/* Socket only for quick reply buttons */}
                      {btn.type === 'quick_reply' && (
                        <Handle
                          type="source"
                          position={Position.Right}
                          id={btn.text} // Use text as ID (payload/reply text)
                          className="!w-4 !h-4 !bg-primary-500 !border-[3px] !border-white !-right-2 transition-all hover:scale-125 hover:!bg-primary-600 shadow-md"
                          style={{ top: '50%' }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <Handle
                  type="source"
                  position={Position.Right}
                  id="default"
                  className="!w-4 !h-4 !bg-primary-500 !border-[3px] !border-white !-right-2 shadow-md"
                />
              )}
            </div>
          </div>
        ) : (
          /* Keyword Trigger View */
          <div className="space-y-3">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
              Active Keywords
            </div>
            <div className="flex flex-wrap gap-1.5">
              {data.keywords?.map((kw, i) => (
                <span
                  key={i}
                  className="px-2 py-1 bg-primary-50 text-primary-700 text-[10px] font-bold rounded-md border border-primary-100 shadow-sm"
                >
                  {kw}
                </span>
              )) || (
                <span className="text-xs text-gray-400 italic">
                  No keywords set
                </span>
              )}
            </div>
            <Handle
              type="source"
              position={Position.Right}
              id="default"
              className="!w-5 !h-5 !bg-primary-500 !border-[3px] !border-white !-right-2.5 shadow-lg hover:scale-110 transition-transform"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TriggerNode;
